// import { EventEmitter } from "events";

import {
  buildWebSocketUrl,
  shouldReconnectAfterWebSocketClose,
  WS_DEFAULT_MAX_RECONNECT_ATTEMPTS,
} from "@/utils/wsAuth";

export enum ConnectionState {
  DISCONNECTED = "DISCONNECTED",
  CONNECTING = "CONNECTING",
  CONNECTED = "CONNECTED",
  RECONNECTING = "RECONNECTING",
}

export interface ConnectionStatus {
  state: ConnectionState;
  lastConnected: Date | null;
  lastError: Error | null;
}

export interface WebSocketMessage {
  id: string;
  type: string;
  payload: any;
  timestamp: number;
  client_id: string;
  client_info?: {
    user_agent?: string;
    platform?: string;
    [key: string]: string | undefined;
  };
}

export interface WebSocketConfig {
  url: string;
  reconnectDelayMs: number;
  maxReconnectAttempts: number;
  debug?: boolean;
  clientId?: string;
  pingIntervalMs?: number;
  authToken?: string;
  disablePing?: boolean;
  channels?: string[]; // Add support for multiple channels
}

// Define event listener types
type StatusChangeListener = (status: ConnectionStatus) => void;
type MessageListener = (message: WebSocketMessage) => void;

/** Avoid the browser warning when tearing down a still-CONNECTING socket. */
function closeWebSocketSafely(ws: WebSocket, reason = "Client disconnect"): void {
  if (ws.readyState === WebSocket.CLOSED || ws.readyState === WebSocket.CLOSING) {
    return;
  }
  if (ws.readyState === WebSocket.OPEN) {
    try {
      ws.close(1000, reason);
    } catch {
      /* ignore */
    }
    return;
  }
  ws.onopen = () => {
    try {
      ws.close(1000, reason);
    } catch {
      /* ignore */
    }
  };
}

export class WebSocketManager {
  private readonly instanceId: string;
  private connections: Map<string, WebSocket> = new Map();
  private config: WebSocketConfig;
  private reconnectAttempts: Map<string, number> = new Map();
  private reconnectTimers: Map<string, number> = new Map();
  private pingTimers: Map<string, number> = new Map();
  private status: Map<string, ConnectionStatus> = new Map();
  private messageQueues: Map<string, WebSocketMessage[]> = new Map();
  private connectionIds: Map<string, number> = new Map();
  private lastPingTimes: Map<string, number> = new Map();
  private lastPongTimes: Map<string, number> = new Map();
  private clientId: string;
  private isLoggingEnabled: boolean;
  private activeChannels: Set<string> = new Set();
  private isConnecting: Set<string> = new Set();
  /** True once a channel's socket has reached OPEN (handshake succeeded). */
  private channelHasOpened: Map<string, boolean> = new Map();

  // Custom event handling
  private statusChangeListeners: Map<string, StatusChangeListener[]> =
    new Map();
  private messageListeners: Map<string, MessageListener[]> = new Map();

  constructor(config: WebSocketConfig) {
    this.instanceId = `wsm_${Date.now()}_${Math.random().toString(36).slice(2, 8)}`;
    this.config = {
      ...config,
      pingIntervalMs: config.pingIntervalMs || 20000,
      disablePing: config.disablePing || false,
      channels: [], // Don't treat base URL as a channel by default
    };
    this.clientId = config.clientId || "client_id_1";

    // Initialize logging based on config.debug and localStorage
    this.isLoggingEnabled = config.debug || false;

    // Only log initialization if logging is enabled
    if (this.isLoggingEnabled || this.checkLocalStorageDebug()) {
      this.log("Initialized with base URL:", this.config.url);
      this.log("Using client ID:", this.clientId);
      this.log("Ping interval:", this.config.pingIntervalMs, "ms");
      this.log("Ping enabled:", !this.config.disablePing);
    }

    // Don't initialize any channels by default
    // Channels will be initialized when explicitly subscribed

    // Attach basic network/visibility resilience hooks
    if (typeof window !== "undefined") {
      try {
        window.addEventListener("online", this.handleOnline);
        window.addEventListener("offline", this.handleOffline);
        document.addEventListener("visibilitychange", this.handleVisibilityChange);
      } catch {
        // Ignore if environment does not allow adding listeners
      }
    }
  }

  public getInstanceId(): string {
    return this.instanceId;
  }
  // Lightweight ping-focused tracing that can be toggled at runtime with localStorage.wsPingTrace = 'true'
  private checkLocalStoragePingTrace(): boolean {
    try {
      if (
        typeof window !== "undefined" &&
        typeof localStorage !== "undefined"
      ) {
        return localStorage.getItem("wsPingTrace") === "true";
      }
    } catch {
      // Silently ignore localStorage access errors (SSR or blocked)
    }
    return false;
  }

  private trace(event: string, channel: string, details?: any): void {
    if (!this.checkLocalStoragePingTrace()) return;
    if (details !== undefined) {
      console.log("[WS-PING]", event, channel, details);
    } else {
      console.log("[WS-PING]", event, channel);
    }
  }


  private initializeChannel(channel: string): void {
    if (!this.activeChannels.has(channel)) {
      const existingStatusListeners = this.statusChangeListeners.get(channel) || [];
      const existingMessageListeners = this.messageListeners.get(channel) || [];
      this.activeChannels.add(channel);
      this.status.set(channel, {
        state: ConnectionState.DISCONNECTED,
        lastConnected: null,
        lastError: null,
      });
      this.messageQueues.set(channel, []);
      this.connectionIds.set(channel, 0);
      // Preserve listeners that may have been attached before subscribe()
      this.statusChangeListeners.set(channel, existingStatusListeners);
      this.messageListeners.set(channel, existingMessageListeners);
    }
  }

  // Event handling methods for specific channels
  public on(
    channel: string,
    event: "statusChange",
    listener: StatusChangeListener
  ): void;
  public on(channel: string, event: "message", listener: MessageListener): void;
  public on(channel: string, event: string, listener: any): void {
    if (event === "statusChange") {
      const listeners = this.statusChangeListeners.get(channel) || [];
      listeners.push(listener as StatusChangeListener);
      this.statusChangeListeners.set(channel, listeners);
    } else if (event === "message") {
      const listeners = this.messageListeners.get(channel) || [];
      listeners.push(listener as MessageListener);
      this.messageListeners.set(channel, listeners);
    }
  }

  // Remove a specific listener for a channel/event
  public off(
    channel: string,
    event: "statusChange" | "message",
    listener: StatusChangeListener | MessageListener
  ): void {
    if (event === "statusChange") {
      const listeners = this.statusChangeListeners.get(channel) || [];
      this.statusChangeListeners.set(
        channel,
        listeners.filter((l) => l !== listener)
      );
    } else if (event === "message") {
      const listeners = this.messageListeners.get(channel) || [];
      this.messageListeners.set(
        channel,
        listeners.filter((l) => l !== listener)
      );
    }
  }

  public removeAllListeners(channel?: string): void {
    if (channel) {
      this.statusChangeListeners.set(channel, []);
      this.messageListeners.set(channel, []);
    } else {
      this.statusChangeListeners.clear();
      this.messageListeners.clear();
    }
  }

  private emit(
    channel: string,
    event: "statusChange",
    status: ConnectionStatus
  ): void;
  private emit(
    channel: string,
    event: "message",
    message: WebSocketMessage
  ): void;
  private emit(channel: string, event: string, data: any): void {
    if (event === "statusChange") {
      const listeners = this.statusChangeListeners.get(channel) || [];
      listeners.forEach((listener) => listener(data));
    } else if (event === "message") {
      const listeners = this.messageListeners.get(channel) || [];
      listeners.forEach((listener) => listener(data));
    }
  }

  public connect(channel?: string): void {
    const channelsToConnect = channel
      ? [channel]
      : Array.from(this.activeChannels);

    channelsToConnect.forEach((ch) => {
      if (this.isConnecting.has(ch)) {
        this.log(`Connect already in progress for ${ch}, skipping.`);
        return;
      }
      // Don't try to connect if we're already connecting or connected
      const ws = this.connections.get(ch);
      if (
        ws &&
        (ws.readyState === WebSocket.CONNECTING ||
          ws.readyState === WebSocket.OPEN)
      ) {
        this.log(
          `Already connected or connecting to ${ch}, ignoring connect call`
        );
        return;
      }

      // Cleanup any existing connection before creating a new one
      this.cleanupExistingConnection(ch);

      // Increment connection ID to track the current connection attempt
      const currentConnectionId = (this.connectionIds.get(ch) || 0) + 1;
      this.connectionIds.set(ch, currentConnectionId);

      // Mark and update status to connecting
      this.isConnecting.add(ch);
      this.channelHasOpened.set(ch, false);
      this.trace("CONNECT_ATTEMPT", ch);
      this.updateStatus(ch, ConnectionState.CONNECTING);

      // Create connection timeout handler
      const connectionTimeoutMs = 10000; // 10 seconds
      const timeoutId = setTimeout(() => {
        const currentConn = this.connections.get(ch);
        if (
          currentConn &&
          currentConn.readyState === WebSocket.CONNECTING &&
          this.connectionIds.get(ch) === currentConnectionId
        ) {
          this.log(
            `Connection attempt to ${ch} timed out after ${connectionTimeoutMs}ms`
          );

          // Force close the connection attempt
          try {
            currentConn.onopen = null;
            currentConn.onclose = null;
            currentConn.onerror = null;
            currentConn.onmessage = null;
            closeWebSocketSafely(currentConn, "Connection timed out");
          } catch (error) {
            this.logError(
              `Error closing timed out connection for ${ch}:`,
              error
            );
          }

          this.connections.delete(ch);

          // Trigger disconnection handling for reconnect logic
          this.handleDisconnect(ch);
        }
      }, connectionTimeoutMs);

      try {
        this.log(`Connecting to ${ch}...`);

        const wsUrl = buildWebSocketUrl(ch, {
          fallbackToken: this.config.authToken,
        });
        const newWs = new WebSocket(wsUrl);
        // Prefer arraybuffer for lower latency ping processing
        try {
          newWs.binaryType = "arraybuffer";
        } catch {
          // ignore if not supported
        }
        this.connections.set(ch, newWs);

        // Set up event handlers with connection timeout handling
        newWs.onopen = (event) => {
          clearTimeout(timeoutId);
          this.handleOpen(ch, currentConnectionId);
        };

        newWs.onclose = (event) => {
          clearTimeout(timeoutId);
          this.handleClose(ch, currentConnectionId, event);
        };

        newWs.onerror = (error) => {
          clearTimeout(timeoutId);
          this.handleError(ch, currentConnectionId, error);
        };

        newWs.onmessage = (event) => {
          // No need to clear timeout as onopen would have been called
          this.handleMessage(ch, currentConnectionId, event);
        };
      } catch (error) {
        clearTimeout(timeoutId);
        this.logError(`Error creating WebSocket for ${ch}:`, error);
        const status = this.status.get(ch);
        if (status) {
          status.lastError =
            error instanceof Error
              ? error
              : new Error("Failed to create WebSocket");
        }
        this.handleDisconnect(ch);
      }
    });
  }

  // Helper method to clean up an existing connection
  private cleanupExistingConnection(channel: string): void {
    const existingWs = this.connections.get(channel);
    if (existingWs) {
      this.log(
        `Cleaning up existing connection for ${channel} before reconnecting`
      );

      try {
        // Remove all listeners to prevent memory leaks and unexpected callbacks
        existingWs.onopen = null;
        existingWs.onclose = null;
        existingWs.onerror = null;
        existingWs.onmessage = null;

        // Close the connection if it's still open
        if (
          existingWs.readyState === WebSocket.OPEN ||
          existingWs.readyState === WebSocket.CONNECTING
        ) {
          closeWebSocketSafely(existingWs);
        }
      } catch (error) {
        this.logError(
          `Error cleaning up existing connection for ${channel}:`,
          error
        );
      }

      // Remove from connections map
      this.connections.delete(channel);
    }
  }

  private handleOpen(channel: string, connectionId: number): void {
    // Check if this is still the current connection attempt
    if (connectionId !== this.connectionIds.get(channel)) {
      this.log(`Ignoring open event from old connection for ${channel}`);
      return;
    }

    this.log(`Connection established for ${channel}`);
    this.trace("OPEN", channel);
    this.isConnecting.delete(channel);
    const status = this.status.get(channel);
    if (status) {
      status.state = ConnectionState.CONNECTED;
      status.lastConnected = new Date();
      this.emit(channel, "statusChange", status);
    }

    this.reconnectAttempts.set(channel, 0);
    this.channelHasOpened.set(channel, true);

    // Process any queued messages
    this.flushQueue(channel);

    // Start the ping interval
    this.startPingInterval(channel);
  }

  private handleClose(
    channel: string,
    connectionId: number,
    event: CloseEvent
  ): void {
    // Verify this is still the current connection
    if (connectionId !== this.connectionIds.get(channel)) {
      this.log(`Ignoring close event for outdated connection on ${channel}`);
      return;
    }

    this.log(`WebSocket closed for ${channel}: ${event.code} ${event.reason}`);
    this.trace("CLOSE", channel, { code: event.code, reason: event.reason });
    this.isConnecting.delete(channel);

    // Stop the ping interval
    this.stopPingInterval(channel);

    // Only attempt reconnect if we're not deliberately disconnecting
    const status = this.status.get(channel);
    if (status && status.state !== ConnectionState.DISCONNECTED) {
      this.handleDisconnect(channel, event);
    }
  }

  private handleError(
    channel: string,
    connectionId: number,
    error: Event
  ): void {
    // Verify this is still the current connection
    if (connectionId !== this.connectionIds.get(channel)) {
      this.log(`Ignoring error event for outdated connection on ${channel}`);
      return;
    }

    this.logError(`WebSocket error for ${channel}:`, error);
    this.isConnecting.delete(channel);
    const status = this.status.get(channel);
    if (status) {
      status.lastError =
        error instanceof Error ? error : new Error("WebSocket error");
    }
  }

  private handleMessage(
    channel: string,
    connectionId: number,
    event: MessageEvent
  ): void {
    // Verify this is still the current connection
    if (connectionId !== this.connectionIds.get(channel)) {
      this.log(`Ignoring message for outdated connection on ${channel}`);
      return;
    }

    // Server sends raw binary ping (b"ping"); reply with binary pong
    if (event.data instanceof ArrayBuffer) {
      const view = new Uint8Array(event.data as ArrayBuffer);
      const isPing =
        view.length === 4 &&
        view[0] === 112 &&
        view[1] === 105 &&
        view[2] === 110 &&
        view[3] === 103; // 'ping'
      if (isPing) {
        this.trace("PING_RECEIVED", channel);
        const ws = this.connections.get(channel);
        if (ws && ws.readyState === WebSocket.OPEN) {
          const pong = new TextEncoder().encode("pong");
            try { ws.send(pong); } catch { /* Silently ignore send errors */ }
            try { ws.send("pong"); } catch { /* Silently ignore send errors */ }
            this.lastPongTimes.set(channel, Date.now());
            this.log(`Replied binary pong on ${channel}`);
            this.trace("PONG_SENT", channel, { type: "binary+text" });
        }
        return;
      }
    } else if (event.data instanceof Blob) {
      const blob: Blob = event.data as Blob;
      blob.arrayBuffer().then((buf) => {
        const view = new Uint8Array(buf);
        // quick check for ASCII 'ping'
        const isPing =
          view.length === 4 &&
          view[0] === 112 &&
          view[1] === 105 &&
          view[2] === 110 &&
          view[3] === 103; // 'p''i''n''g'
        if (isPing) {
          this.trace("PING_RECEIVED", channel);
          const ws = this.connections.get(channel);
          if (ws && ws.readyState === WebSocket.OPEN) {
            const pong = new TextEncoder().encode("pong");
            try { ws.send(pong); } catch { /* Silently ignore send errors */ }
            try { ws.send("pong"); } catch { /* Silently ignore send errors */ }
            this.lastPongTimes.set(channel, Date.now());
            this.log(`Replied binary pong on ${channel}`);
            this.trace("PONG_SENT", channel, { type: "binary+text" });
          }
          return;
        }
        // Non-ping binary payload: ignore
      });
      return;
    }

    // Text frame (JSON or plain text from server)
    try {
      const rawText = (event.data as string) ?? "";
      const trimmed = rawText.trim();

      // Plain text ping
      if (trimmed.toLowerCase() === "ping") {
        const ws = this.connections.get(channel);
        if (ws && ws.readyState === WebSocket.OPEN) {
          try { ws.send("pong"); } catch { /* Silently ignore send errors */ }
          this.lastPongTimes.set(channel, Date.now());
          this.log(`Replied text pong on ${channel}`);
          this.trace("PONG_SENT", channel, { type: "text" });
        }
        return;
      }

      // Try JSON
      const message = JSON.parse(rawText);
      const label = message.type || message.status;

      // JSON-wrapped ping
      if (typeof label === "string" && label.toLowerCase() === "ping") {
        const ws = this.connections.get(channel);
        if (ws && ws.readyState === WebSocket.OPEN) {
          try { ws.send("pong"); } catch { /* Silently ignore send errors */ }
          this.lastPongTimes.set(channel, Date.now());
          this.log(`Replied JSON ping with pong on ${channel}`);
          this.trace("PONG_SENT", channel, { type: "json" });
        }
        return;
      }

      // Avoid spamming logs for handshakes
      if (label !== "connected") {
        this.log(`Received message on ${channel}:`, label);
      }
      this.emit(channel, "message", message);
    } catch (error) {
      this.logError(`Error parsing message for ${channel}:`, error);
    }
  }

  public disconnect(channel?: string): void {
    const channelsToDisconnect = channel
      ? [channel]
      : Array.from(this.activeChannels);

    channelsToDisconnect.forEach((ch) => {
      this.log(`Disconnecting WebSocket for ${ch}`);
      this.updateStatus(ch, ConnectionState.DISCONNECTED);

      // Stop the ping interval
      this.stopPingInterval(ch);

      const reconnectTimer = this.reconnectTimers.get(ch);
      if (reconnectTimer !== null) {
        clearTimeout(reconnectTimer);
        this.reconnectTimers.delete(ch);
      }

      this.reconnectAttempts.delete(ch);

      const ws = this.connections.get(ch);
      if (ws) {
        // Remove all listeners to prevent memory leaks
        ws.onopen = null;
        ws.onclose = null;
        ws.onerror = null;
        ws.onmessage = null;

        if (
          ws.readyState === WebSocket.OPEN ||
          ws.readyState === WebSocket.CONNECTING
        ) {
          try {
            closeWebSocketSafely(ws);
          } catch (error) {
            this.logError(`Error closing WebSocket for ${ch}:`, error);
          }
        }

        this.connections.delete(ch);
      }
    });
  }

  public send(
    channel: string,
    message: Omit<
      WebSocketMessage,
      "id" | "timestamp" | "client_id" | "client_info"
    >
  ): void {
    // Validate that the message has a type field
    if (!message.type) {
      this.logError(
        `Error: Message must have a 'type' field for ${channel}`,
        message
      );
      return;
    }

    const fullMessage: WebSocketMessage = {
      ...message,
      id: `${Date.now()}-${Math.random().toString(36).substring(2, 9)}`,
      timestamp: Date.now(),
      client_id: this.clientId,
      client_info: {
        user_agent:
          typeof navigator !== "undefined" ? navigator.userAgent : "unknown",
        platform:
          typeof navigator !== "undefined" ? navigator.platform : "unknown",
      },
    };

    const ws = this.connections.get(channel);
    if (ws && ws.readyState === WebSocket.OPEN) {
      this.log(`Sending message on ${channel}:`, fullMessage.type);
      ws.send(JSON.stringify(fullMessage));
    } else {
      this.log(
        `Queueing message for ${channel} (not connected):`,
        fullMessage.type
      );
      const queue = this.messageQueues.get(channel) || [];
      queue.push(fullMessage);
      this.messageQueues.set(channel, queue);

      // If we're not connecting or reconnecting, try to connect
      const status = this.status.get(channel);
      if (status?.state === ConnectionState.DISCONNECTED) {
        this.connect(channel);
      }
    }
  }

  public getStatus(channel: string): ConnectionStatus {
    const status = this.status.get(channel);
    return status
      ? { ...status }
      : {
          state: ConnectionState.DISCONNECTED,
          lastConnected: null,
          lastError: null,
        };
  }

  private updateStatus(channel: string, state: ConnectionState): void {
    const status = this.status.get(channel);
    if (status && status.state !== state) {
      this.log(`Status changed for ${channel}: ${status.state} -> ${state}`);
      status.state = state;
      this.emit(channel, "statusChange", this.getStatus(channel));
    }
  }

  private handleDisconnect(channel: string, closeEvent?: CloseEvent): void {
    // Clean up the current connection
    this.cleanupExistingConnection(channel);
    this.isConnecting.delete(channel);

    // Stop the ping interval
    this.stopPingInterval(channel);

    const hadOpened = this.channelHasOpened.get(channel) === true;
    this.channelHasOpened.set(channel, false);

    // If we should reconnect
    const attempts = this.reconnectAttempts.get(channel) || 0;
    const status = this.status.get(channel);

    const maxAttempts =
      this.config.maxReconnectAttempts ?? WS_DEFAULT_MAX_RECONNECT_ATTEMPTS;
    // For closeEvent, check if handshake completed (hadOpened).
    // For client-side timeout / network errors (closeEvent omitted), allow reconnect with backoff.
    const isExplicitClose = closeEvent !== undefined;
    const mayReconnect =
      maxAttempts > 0 &&
      (!isExplicitClose ||
        shouldReconnectAfterWebSocketClose(
          closeEvent.code,
          closeEvent.reason,
          { hadOpened },
        ));

    if (mayReconnect && attempts < maxAttempts && status?.state !== ConnectionState.DISCONNECTED) {
      this.reconnectAttempts.set(channel, attempts + 1);
      this.updateStatus(channel, ConnectionState.RECONNECTING);

      // Calculate backoff delay - start with base delay and add exponential backoff
      const baseDelay = this.config.reconnectDelayMs;
      const backoff = Math.min(Math.pow(2, attempts), 6) * baseDelay; // Cap at 64x base delay
      const jitter = Math.random() * 2000; // Add up to 2s jitter
      const delay = baseDelay + backoff + jitter;

      this.log(
        `Scheduling reconnect for ${channel} in ${Math.round(delay)}ms (attempt ${
          attempts + 1
        } of ${maxAttempts})`
      );

      const timer = window.setTimeout(() => {
        const currentStatus = this.status.get(channel);
        if (currentStatus?.state === ConnectionState.RECONNECTING) {
          this.connect(channel);
        }
      }, delay);

      this.reconnectTimers.set(channel, timer);
    } else if (status?.state !== ConnectionState.DISCONNECTED) {
      this.log(`Max reconnect attempts reached for ${channel}, giving up`);
      this.updateStatus(channel, ConnectionState.DISCONNECTED);
    }
  }

  // Network/visibility handlers
  private handleOnline = (): void => {
    this.log("Network online: ensuring WebSocket connections are active");
    const channels = Array.from(this.activeChannels);
    channels.forEach((ch) => {
      const ws = this.connections.get(ch);
      const ready = ws?.readyState;
      if (ready !== WebSocket.OPEN && ready !== WebSocket.CONNECTING) {
        this.connect(ch);
      }
    });
  };

  private handleOffline = (): void => {
    this.log("Network offline: sockets may be interrupted");
  };

  private handleVisibilityChange = (): void => {
    try {
      if (document.visibilityState === "visible") {
        this.log("Tab visible: verifying WebSocket connectivity");
        const channels = Array.from(this.activeChannels);
        channels.forEach((ch) => {
          const ws = this.connections.get(ch);
          if (!ws || ws.readyState !== WebSocket.OPEN) {
            this.connect(ch);
          } else if (!this.config.disablePing) {
            this.sendPing(ch);
          }
        });
      }
    } catch {
      // no-op
    }
  };

  // Explicit destroy to detach global listeners and close sockets
  public destroy(): void {
    try {
      if (typeof window !== "undefined") {
        window.removeEventListener("online", this.handleOnline);
        window.removeEventListener("offline", this.handleOffline);
        document.removeEventListener(
          "visibilitychange",
          this.handleVisibilityChange
        );
      }
    } catch {
      // ignore
    }
    try {
      this.removeAllListeners();
      this.disconnect();
    } catch {
      // ignore
    }
  }

  private flushQueue(channel: string): void {
    const queue = this.messageQueues.get(channel) || [];
    if (queue.length === 0) return;

    this.log(`Flushing ${queue.length} queued messages for ${channel}`);
    this.messageQueues.set(channel, []);

    for (const message of queue) {
      this.send(channel, message);
    }
  }

  private log(...args: any[]): void {
    // Suppress general logs if ping-trace is enabled for clarity
    if (this.checkLocalStoragePingTrace()) {
      return;
    }
    // Only log if either debug flag is enabled or wsDebug is set
    const shouldLog = this.isLoggingEnabled || this.checkLocalStorageDebug();


  }

  // Error logging method that logs if debug is enabled or for critical errors
  private logError(...args: any[]): void {
    if (this.checkLocalStoragePingTrace()) {
      // Suppress noisy errors when ping-trace is enabled; close/error lines shown via trace
      return;
    }
    // For errors, we'll still respect the localStorage setting
    // but always log critical errors regardless of debug settings
    const isCriticalError =
      args.some(
        (arg) =>
          typeof arg === "string" &&
          (arg.includes("ECONNREFUSED") || arg.includes("Failed to connect"))
      ) ||
      args.some(
        (arg) =>
          arg instanceof Error &&
          (arg.message.includes("ECONNREFUSED") ||
            arg.message.includes("Failed to connect"))
      );

    if (
      isCriticalError ||
      this.isLoggingEnabled ||
      this.checkLocalStorageDebug()
    ) {
      console.error("[WebSocketManager]", ...args);
    }
  }

  // Check if localStorage.wsDebug is set to 'true'
  private checkLocalStorageDebug(): boolean {
    try {
      if (
        typeof window !== "undefined" &&
        typeof localStorage !== "undefined"
      ) {
        return localStorage.getItem("wsDebug") === "true";
      }
    } catch {
      // Ignore errors accessing localStorage (e.g., in SSR or when blocked)
    }
    return false;
  }

  // Methods to control logging
  public enableLogging(): void {
    this.isLoggingEnabled = true;
    this.log("Logging enabled");
  }

  public disableLogging(): void {
    this.log("Logging disabled");
    this.isLoggingEnabled = false;
  }

  public isLogging(): boolean {
    // Return true if either internal flag or localStorage is set
    return this.isLoggingEnabled || this.checkLocalStorageDebug();
  }

  // Ping methods
  private startPingInterval(channel: string): void {
    if (this.config.disablePing) {
      this.log(
        `Ping mechanism is disabled for ${channel}, skipping ping interval`
      );
      return;
    }

    this.stopPingInterval(channel); // Clear any existing interval

    this.log(
      `Starting ping interval for ${channel} (${this.config.pingIntervalMs}ms)`
    );
    const timer = window.setInterval(() => {
      this.sendPing(channel);
    }, this.config.pingIntervalMs);

    this.pingTimers.set(channel, timer);
  }

  private stopPingInterval(channel: string): void {
    const timer = this.pingTimers.get(channel);
    if (timer !== undefined) {
      this.log(`Stopping ping interval for ${channel}`);
      clearInterval(timer);
      this.pingTimers.delete(channel);
    }
  }

  private sendPing(channel: string): void {
    const ws = this.connections.get(channel);
    if (ws && ws.readyState === WebSocket.OPEN) {
      const pingTime = Date.now();
      this.lastPingTimes.set(channel, pingTime);
      this.log(`Sending ping to check connection for ${channel}`);

      this.send(channel, {
        type: "ping",
        payload: {
          timestamp: pingTime,
        },
      });
    }
  }

  // Public method to manually send a ping to a specific channel
  public ping(channel: string): void {
    this.sendPing(channel);
  }

  // Method to subscribe to a new channel
  public subscribe(channel: string): void {
    // Ensure channel has the correct protocol when on HTTPS
    let channelUrl = channel;
    if (
      typeof window !== "undefined" &&
      window.location.protocol === "https:" &&
      channelUrl.startsWith("ws://")
    ) {
      channelUrl = channelUrl.replace("ws://", "wss://");
    }

    if (!this.activeChannels.has(channelUrl)) {
      this.initializeChannel(channelUrl);
      this.connect(channelUrl);
    }
  }

  // Method to unsubscribe from a channel
  public unsubscribe(channel: string): void {
    // Ensure we check for the secure version of the channel if needed
    let channelUrl = channel;
    if (
      typeof window !== "undefined" &&
      window.location.protocol === "https:" &&
      channelUrl.startsWith("ws://")
    ) {
      channelUrl = channelUrl.replace("ws://", "wss://");
    }

    if (channelUrl === this.config.url) {
      this.log(`Cannot unsubscribe from main channel: ${channelUrl}`);
      return;
    }

    // Fully tear down the channel and its connection
    try {
      const ws = this.connections.get(channelUrl);
      if (ws) {
        try {
          ws.onopen = null;
          ws.onclose = null;
          ws.onerror = null;
          ws.onmessage = null;
        } catch { /* Silently ignore errors clearing handlers */ }
        try { closeWebSocketSafely(ws); } catch { /* Silently ignore close errors */ }
        this.connections.delete(channelUrl);
      }
    } catch { /* Silently ignore cleanup errors */ }
    this.disconnect(channelUrl);
    this.activeChannels.delete(channelUrl);
    this.status.delete(channelUrl);
    this.messageQueues.delete(channelUrl);
    this.connectionIds.delete(channelUrl);
    this.statusChangeListeners.delete(channelUrl);
    this.messageListeners.delete(channelUrl);
  }
}
